import { useState, useEffect, useCallback } from 'react';
import { formatters } from '../../utils/formatters';
import LoadingSpinner from '../common/LoadingSpinner';
import CepField from '../common/CepField';
import { Save } from 'lucide-react';
import { validators } from '../../utils/validators';

const CustomerForm = ({ customer, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    document: '',
    phone: '',
    email: '',
    emails: [], // Array para múltiplos e-mails
    birthDate: '',
    referralCode: '',
    // Campos de endereço opcionais
    cep: '',
    street: '',
    number: '',
    complement: '',
    city: '',
    state: '',
    neighborhood: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (customer) {
      // Processar e-mails existentes
      let emailsArray = [];
      let primaryEmail = '';
      
      if (customer.emails && Array.isArray(customer.emails)) {
        emailsArray = customer.emails;
        primaryEmail = customer.emails[0] || '';
      } else if (customer.email) {
        primaryEmail = customer.email;
        emailsArray = [customer.email];
      }
      
      setFormData({
        name: customer.name || '',
        document: customer.cpf || customer.document || '',
        phone: customer.phone || '',
        email: primaryEmail,
        emails: emailsArray,
        birthDate: formatters.formatDateForInput(customer.birthDate),
        referralCode: customer.referralCode || '',
        // Campos de endereço
        cep: customer.cep || '',
        street: customer.street || '',
        number: customer.number || '',
        complement: customer.complement || '',
        city: customer.city || '',
        state: customer.state || '',
        neighborhood: customer.neighborhood || ''
      });
    } else {
      // Resetar formulário para novo cliente
      setFormData({
        name: '',
        document: '',
        phone: '',
        email: '',
        emails: [''], // Inicializar com array contendo um e-mail vazio
        birthDate: '',
        referralCode: '',
        // Campos de endereço opcionais
        cep: '',
        street: '',
        number: '',
        complement: '',
        city: '',
        state: '',
        neighborhood: ''
      });
    }
  }, [customer]);

  const validateForm = () => {
    const newErrors = {};

    if (!validators.isValidName(formData.name)) {
      newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
    }

    if (!validators.isValidCpfOrCnpj(formData.document)) {
      const docType = validators.getDocumentType(formData.document);
      if (docType === 'unknown') {
        newErrors.document = 'CPF ou CNPJ inválido';
      } else {
        newErrors.document = `${docType === 'cpf' ? 'CPF' : 'CNPJ'} inválido`;
      }
    }

    if (!validators.isValidPhone(formData.phone)) {
      const cleanPhone = formatters.cleanPhone(formData.phone);
      if (!cleanPhone || cleanPhone.length === 0) {
        newErrors.phone = 'Telefone é obrigatório';
      } else if (cleanPhone.length < 10 || cleanPhone.length > 11) {
        newErrors.phone = 'Telefone deve ter 10 ou 11 dígitos (formato: (11) 99999-9999)';
      } else {
        newErrors.phone = 'Telefone inválido (formato: (11) 99999-9999)';
      }
    }

    // Validação de e-mails
    if (formData.email && !validators.isValidEmail(formData.email)) {
      newErrors.email = 'E-mail principal inválido';
    }
    
    // Validar e-mails adicionais
    const emails = formData.emails || [];
    if (emails.length > 1) {
      const invalidEmails = emails.filter(email => email && !validators.isValidEmail(email));
      if (invalidEmails.length > 0) {
        newErrors.emails = 'Alguns e-mails adicionais são inválidos';
      }
      
      // Verificar duplicatas
      const emailSet = new Set(emails.filter(email => email));
      if (emailSet.size !== emails.filter(email => email).length) {
        newErrors.emails = 'Existem e-mails duplicados na lista';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Preparar lista de e-mails limpa (sem valores vazios)
      const emails = formData.emails || [];
      const cleanEmails = emails.filter(email => email && email.trim());
      
      // Garantir que o e-mail principal esteja na lista se existir
      const primaryEmail = formData.email && formData.email.trim();
      if (primaryEmail && !cleanEmails.includes(primaryEmail)) {
        cleanEmails.unshift(primaryEmail);
      }
      
      await onSubmit({
        ...formData,
        cpf: formatters.cleanCpfOrCnpj(formData.document),
        phone: formatters.cleanPhone(formData.phone),
        birthDate: formatters.parseDateFromInput(formData.birthDate),
        emails: cleanEmails.length > 0 ? cleanEmails : (primaryEmail ? [primaryEmail] : []),
        email: primaryEmail || (cleanEmails.length > 0 ? cleanEmails[0] : '')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentChange = (e) => {
    const formatted = formatters.formatCpfOrCnpj(e.target.value);
    setFormData({ ...formData, document: formatted });
  };

  const handlePhoneChange = (e) => {
    const formatted = formatters.formatPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const handleCepChange = useCallback((cepValue) => {
    setFormData({ ...formData, cep: cepValue });
  }, [formData]);

  const handleAddressChange = useCallback((addressData) => {
    // Preencher automaticamente os campos de endereço com os dados do CEP
    setFormData(prev => ({
      ...prev,
      street: addressData.endereco || prev.street,
      neighborhood: addressData.bairro || prev.neighborhood,
      city: addressData.cidade || prev.city,
      state: addressData.estado || prev.state
    }));
  }, []);

  // Gerenciar múltiplos e-mails
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    const currentEmails = formData.emails || [];
    const updatedEmails = [...currentEmails];
    
    // Garantir que há pelo menos uma posição no array
    if (updatedEmails.length === 0) {
      updatedEmails.push('');
    }
    
    updatedEmails[0] = newEmail; // O primeiro e-mail é sempre o principal
    
    setFormData({ 
      ...formData, 
      email: newEmail,
      emails: updatedEmails
    });
  };

  const addEmail = () => {
    const currentEmails = formData.emails || [];
    setFormData({
      ...formData,
      emails: [...currentEmails, '']
    });
  };

  const removeEmail = (index) => {
    if (index === 0) return; // Não pode remover o e-mail principal
    const currentEmails = formData.emails || [];
    const updatedEmails = currentEmails.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      emails: updatedEmails
    });
  };

  const handleAdditionalEmailChange = (index, value) => {
    const currentEmails = formData.emails || [];
    const updatedEmails = [...currentEmails];
    
    // Garantir que o array tenha o tamanho necessário
    while (updatedEmails.length <= index) {
      updatedEmails.push('');
    }
    
    updatedEmails[index] = value;
    
    // Se for o primeiro e-mail, atualizar também o campo principal
    const updatedData = {
      ...formData,
      emails: updatedEmails
    };
    
    if (index === 0) {
      updatedData.email = value;
    }
    
    setFormData(updatedData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nome *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.name ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Digite o nome completo"
        />
        {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          CPF/CNPJ *
        </label>
        <input
          type="text"
          value={formData.document}
          onChange={handleDocumentChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.document ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="000.000.000-00 ou 00.000.000/0000-00"
          maxLength="18"
        />
        {errors.document && <p className="text-sm text-red-600 mt-1">{errors.document}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Digite o CPF (pessoa física) ou CNPJ (pessoa jurídica)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Telefone *
        </label>
        <input
          type="text"
          value={formData.phone}
          onChange={handlePhoneChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.phone ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="(11) 99999-9999"
          maxLength="15"
        />
        {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          E-mails
        </label>
        
        {/* E-mail principal */}
        <div className="space-y-2">
          <input
            type="email"
            value={formData.email}
            onChange={handleEmailChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.email ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="cliente@email.com (principal)"
          />
          {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
        </div>

        {/* E-mails adicionais */}
        {(formData.emails || []).length > 1 && (
          <div className="mt-3 space-y-2">
            <label className="block text-xs font-medium text-gray-600">
              E-mails adicionais:
            </label>
            {(formData.emails || []).slice(1).map((email, index) => (
              <div key={index + 1} className="flex items-center space-x-2">
                <input
                  type="email"
                  value={email || ''}
                  onChange={(e) => handleAdditionalEmailChange(index + 1, e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.emails ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="email.adicional@exemplo.com"
                />
                <button
                  type="button"
                  onClick={() => removeEmail(index + 1)}
                  className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Botão para adicionar e-mail */}
        <button
          type="button"
          onClick={addEmail}
          className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          + Adicionar outro e-mail
        </button>

        {errors.emails && <p className="text-sm text-red-600 mt-1">{errors.emails}</p>}
        
        <p className="text-xs text-gray-500 mt-1">
          O primeiro e-mail será usado como principal para comunicações
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Data de Nascimento
        </label>
        <input
          type="date"
          value={formData.birthDate}
          onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.birthDate ? 'border-red-300' : 'border-gray-300'
          }`}
        />
        {errors.birthDate && <p className="text-sm text-red-600 mt-1">{errors.birthDate}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Código de Indicação (opcional)
        </label>
        <input
          type="text"
          value={formData.referralCode}
          onChange={(e) => setFormData({ ...formData, referralCode: e.target.value })}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.referralCode ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Digite o código de quem indicou este cliente"
        />
        {errors.referralCode && <p className="text-sm text-red-600 mt-1">{errors.referralCode}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Se este cliente foi indicado, digite o código para gerar pontos extras para ambos
        </p>
      </div>

      {/* Seção de Endereço */}
      <div className="border-t pt-4 mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Endereço (opcional)</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CEP
            </label>
            <CepField
              value={formData.cep}
              onChange={handleCepChange}
              onAddressChange={handleAddressChange}
              placeholder="00000-000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Número
            </label>
            <input
              type="text"
              value={formData.number}
              onChange={(e) => setFormData({ ...formData, number: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="123"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rua/Logradouro
          </label>
          <input
            type="text"
            value={formData.street}
            onChange={(e) => setFormData({ ...formData, street: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Nome da rua, avenida, etc."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bairro
            </label>
            <input
              type="text"
              value={formData.neighborhood}
              onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Bairro"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cidade
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Cidade"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estado
            </label>
            <input
              type="text"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="UF"
              maxLength="2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Complemento
            </label>
            <input
              type="text"
              value={formData.complement}
              onChange={(e) => setFormData({ ...formData, complement: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Apartamento, sala, etc."
            />
          </div>
        </div>
      </div>

      <div className="flex space-x-3 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          <span>{customer ? 'Atualizar' : 'Criar'}</span>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
};

export default CustomerForm;